import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Mail,
  Plus,
  Edit,
  Trash2,
  Send,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye
} from 'lucide-react';

const EmailPage = () => {
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Template form
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body_html: '',
    body_text: ''
  });
  
  // Preview
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, jobsRes, emailsRes] = await Promise.all([
        api.get('/templates'),
        api.get('/emails/jobs'),
        api.get('/emails/sent?limit=50')
      ]);
      
      setTemplates(templatesRes.data);
      setJobs(jobsRes.data);
      setSentEmails(emailsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate) {
        await api.put(`/templates/${editingTemplate.template_id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/templates', templateForm);
        toast.success('Template created');
      }
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', body_html: '', body_text: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body_html: template.body_html,
      body_text: template.body_text || ''
    });
    setShowTemplateDialog(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/templates/${templateId}`);
      toast.success('Template deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handlePreviewTemplate = (template) => {
    setPreviewContent({
      subject: template.subject,
      body: template.body_html
    });
    setShowPreviewDialog(true);
  };

  const getJobStatusBadge = (status) => {
    switch (status) {
      case 'queued':
        return <Badge variant="secondary"><Clock className="mr-1 h-3 w-3" />Queued</Badge>;
      case 'processing':
        return <Badge variant="default" className="bg-blue-500"><RefreshCw className="mr-1 h-3 w-3 animate-spin" />Processing</Badge>;
      case 'completed':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="mr-1 h-3 w-3" />Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="mr-1 h-3 w-3" />Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const mergeFieldsHelp = `
Available merge fields:
• {first_name} - Contact's first name
• {last_name} - Contact's last name
• {loan_type} - Loan types interested in
• {property_address} - Property address
• {company} - Contact's company
• {phone} - Contact's phone
• {agent_name} - Your name
  `.trim();

  return (
    <div className="flex min-h-screen bg-background" data-testid="email-page">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Mail className="h-8 w-8" />
              Email
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage templates and track bulk outreach
            </p>
          </div>
        </div>

        <Tabs defaultValue="templates" className="space-y-6">
          <TabsList>
            <TabsTrigger value="templates" data-testid="templates-tab">Templates</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="jobs-tab">Bulk Jobs</TabsTrigger>
            <TabsTrigger value="sent" data-testid="sent-tab">Sent Emails</TabsTrigger>
          </TabsList>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Email Templates</CardTitle>
                    <CardDescription>Create and manage reusable email templates</CardDescription>
                  </div>
                  <Dialog open={showTemplateDialog} onOpenChange={(open) => {
                    setShowTemplateDialog(open);
                    if (!open) {
                      setEditingTemplate(null);
                      setTemplateForm({ name: '', subject: '', body_html: '', body_text: '' });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button data-testid="create-template-btn">
                        <Plus className="mr-2 h-4 w-4" />
                        Create Template
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl">
                      <DialogHeader>
                        <DialogTitle>
                          {editingTemplate ? 'Edit Template' : 'Create New Template'}
                        </DialogTitle>
                        <DialogDescription>
                          Use merge fields to personalize your emails
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid grid-cols-3 gap-6 py-4">
                        <div className="col-span-2 space-y-4">
                          <div className="space-y-2">
                            <Label>Template Name</Label>
                            <Input
                              value={templateForm.name}
                              onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                              placeholder="e.g., DSCR Follow-up"
                              data-testid="template-name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Subject Line</Label>
                            <Input
                              value={templateForm.subject}
                              onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                              placeholder="e.g., Hi {first_name}, about your {loan_type} loan..."
                              data-testid="template-subject"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Email Body (HTML)</Label>
                            <Textarea
                              value={templateForm.body_html}
                              onChange={(e) => setTemplateForm({...templateForm, body_html: e.target.value})}
                              placeholder="Write your email content..."
                              rows={10}
                              data-testid="template-body"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Merge Fields</Label>
                          <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap font-mono">
                            {mergeFieldsHelp}
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleSaveTemplate} 
                          disabled={!templateForm.name || !templateForm.subject || !templateForm.body_html}
                          data-testid="save-template-btn"
                        >
                          {editingTemplate ? 'Update Template' : 'Create Template'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {templates.length > 0 ? (
                  <div className="grid gap-4">
                    {templates.map((template) => (
                      <div
                        key={template.template_id}
                        className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        data-testid={`template-${template.template_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">{template.name}</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              Subject: {template.subject}
                            </p>
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                              {template.body_html?.replace(/<[^>]*>/g, '').substring(0, 150)}...
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePreviewTemplate(template)}
                              data-testid={`preview-template-${template.template_id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditTemplate(template)}
                              data-testid={`edit-template-${template.template_id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteTemplate(template.template_id)}
                              data-testid={`delete-template-${template.template_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No templates yet</p>
                    <p className="text-sm">Create your first email template to get started</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bulk Jobs Tab */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle>Bulk Email Jobs</CardTitle>
                <CardDescription>Track your bulk email campaigns</CardDescription>
              </CardHeader>
              <CardContent>
                {jobs.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Sent</TableHead>
                        <TableHead>Failed</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobs.map((job) => (
                        <TableRow key={job.job_id} data-testid={`job-${job.job_id}`}>
                          <TableCell className="font-mono text-sm">{job.job_id}</TableCell>
                          <TableCell>{getJobStatusBadge(job.status)}</TableCell>
                          <TableCell>{job.total_targets}</TableCell>
                          <TableCell className="text-green-600">{job.sent_count}</TableCell>
                          <TableCell className="text-red-600">{job.failed_count}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(job.created_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Send className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No bulk jobs yet</p>
                    <p className="text-sm">Start a bulk email campaign from the Contacts page</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sent Emails Tab */}
          <TabsContent value="sent">
            <Card>
              <CardHeader>
                <CardTitle>Sent Emails</CardTitle>
                <CardDescription>View all sent emails</CardDescription>
              </CardHeader>
              <CardContent>
                {sentEmails.length > 0 ? (
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>To</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Sent At</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sentEmails.map((email) => (
                          <TableRow key={email.email_id} data-testid={`sent-email-${email.email_id}`}>
                            <TableCell>{email.to_email}</TableCell>
                            <TableCell className="max-w-[300px] truncate">{email.subject}</TableCell>
                            <TableCell>
                              <Badge variant={email.status === 'sent' ? 'default' : 'destructive'}>
                                {email.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {new Date(email.sent_at).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No emails sent yet</p>
                    <p className="text-sm">Emails you send will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Template Preview</DialogTitle>
            </DialogHeader>
            {previewContent && (
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-muted-foreground">Subject</Label>
                  <p className="font-medium mt-1">{previewContent.subject}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Body</Label>
                  <div 
                    className="mt-2 p-4 bg-muted rounded-lg prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: previewContent.body }}
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default EmailPage;
